import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {MapComponent as OriginalMapComponent, NgxMapboxGLModule} from "ngx-mapbox-gl";
import {SharedModule} from "../../../shared/shared.module";
import mapboxgl, {AnyLayer} from "mapbox-gl";
import {Subscription} from "rxjs";
import {Point} from "../../../models/point";
import {LocationHelper} from "../../../utils/location-helper";

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [
    SharedModule,
    NgxMapboxGLModule,
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('map') map?: OriginalMapComponent;
  layerQueue: AnyLayer[] = [];
  markerQueue: mapboxgl.Marker[] = [];
  clickQueue: { id: string, callback: (event: mapboxgl.MapMouseEvent) => any }[] = [];
  subscriptions: Subscription[] = [];
  loaded = false;
  allPoints: Point[] = [];

  ngAfterViewInit() {
    if (this.map) {
      this.subscriptions.push(
        this.map.mapLoad.subscribe(() => {
          this.layerQueue.forEach(layer => {
            this.map!.mapInstance.addLayer(layer);
          });

          this.markerQueue.forEach(marker => {
            marker.addTo(this.map!.mapInstance);
          });

          this.clickQueue.forEach(({ id, callback }) => {
            this.map!.mapInstance.on('click', id, callback);
          });

          this.zoomToFit();

          // Insert the layer beneath any symbol layer.
          const map = this.map!.mapInstance;
          this.add3DLayer(map);

          this.layerQueue = [];
          this.loaded = true;
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  createPolyline(id: string, list: Point[], onClick?: (event: mapboxgl.MapMouseEvent) => any, smooth: boolean = true, color: string = '#ff7200') {
    const helper = new LocationHelper();

    const originalList = list;
    if (smooth) {
      list = helper.smoothPoints(list, 3);
    }

    const layer: AnyLayer = {
      id: id,
      type: 'line',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: list.map(point => [point.lng, point.lat])
          }
        }
      },
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': color,
        'line-width': 8
      }
    };

    this.allPoints = [...this.allPoints, ...list];

    const smoothHandler = (event: mapboxgl.MapMouseEvent) => {
      // If smoothing is enabled, add 'click' event listener to the smoothed line with the original list
      // But, only if Ctrl/Cmd key is pressed
      if (smooth && (event.originalEvent.ctrlKey || event.originalEvent.metaKey)) {
        // First, check if the original line is already added
        const originalLayer = this.map!.mapInstance.getLayer(`${id}-original`);

        if (originalLayer) {
          return;
        }

        this.createPolyline(`${id}-original`, originalList, () => {
          // Remove the original line when clicked
          // Remove the source as well
          this.map!.mapInstance.removeLayer(`${id}-original`);
          this.map!.mapInstance.removeSource(`${id}-original`);
        }, false, 'rgba(255, 0, 0, .5)');
      }
    }

    if (this.loaded) {
      this.map!.mapInstance.addLayer(layer);

      if (onClick) {
        this.map!.mapInstance.on('click', id, onClick);
      }

      this.map!.mapInstance.on('click', id, smoothHandler);
    } else {
      this.layerQueue.push(layer);

      if (onClick) {
        this.clickQueue.push({ id, callback: onClick });
      }

      this.clickQueue.push({ id, callback: smoothHandler });
    }
  }

  createMarker(point: Point, onClick?: () => any, customImage?: string) {
    const marker = new mapboxgl.Marker()
      .setLngLat([point.lng, point.lat]);

    if (customImage) {
      marker.getElement().innerHTML = '<img src="' + customImage + '" style="width: 30px; height: 30px; border-radius: 50%;"/>';
    }

    if (onClick) {
      marker.getElement().addEventListener('click', onClick);
    }

    if (this.loaded) {
      marker.addTo(this.map!.mapInstance);
    } else {
      this.markerQueue.push(marker);
    }

    this.allPoints.push(point);
  }

  zoomToFit() {
    if (!this.map) {
      return;
    }

    const list = this.allPoints;

    if (this.allPoints.length === 0) {
      return;
    }

    const bounds = list.reduce((bounds, point) => {
      return bounds.extend([point.lng, point.lat]);
    }, new mapboxgl.LngLatBounds(list[0], list[0]));

    this.map.mapInstance.fitBounds(bounds, {
      padding: 40,
      duration: 500,
    });
  }

  add3DLayer(map: mapboxgl.Map) {
    const layers = map.getStyle().layers;
    const labelLayerId = layers.find(
      (layer) => layer.type === 'symbol' && layer.layout!['text-field']
    )?.id ?? '';

    // The 'building' layer in the Mapbox Streets
    // vector tileset contains building height data
    // from OpenStreetMap.
    map.addLayer(
      {
        'id': 'add-3d-buildings',
        'source': 'composite',
        'source-layer': 'building',
        'filter': ['==', 'extrude', 'true'],
        'type': 'fill-extrusion',
        'minzoom': 15,
        'paint': {
          'fill-extrusion-color': '#aaa',

          // Use an 'interpolate' expression to
          // add a smooth transition effect to
          // the buildings as the user zooms in.
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'height']
          ],
          'fill-extrusion-base': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15,
            0,
            15.05,
            ['get', 'min_height']
          ],
          'fill-extrusion-opacity': 0.6
        }
      },
      labelLayerId
    );
  }
}
